"use client";

import { DateTime } from "luxon";
import { useEffect } from "react";

const LAST_RELOAD_KEY = "netic:stale-build-reload-at";
const RELOAD_COOLDOWN_MS = 60_000;

/** Check that an error means this tab's build is gone from the server (webpack or Turbopack chunk). */
export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }
  return error.name === "ChunkLoadError" || error.message.startsWith("Failed to load chunk ");
}

/**
 * Reload the page onto the current build after a chunk fails to load, at most once per cooldown
 * so a chunk that is genuinely missing on the current build cannot cause a reload loop.
 */
export function reloadOnceForStaleBuild(error: unknown): boolean {
  if (!isChunkLoadError(error)) {
    return false;
  }
  const now = DateTime.now().toMillis();
  try {
    const last = Number(window.sessionStorage.getItem(LAST_RELOAD_KEY) ?? 0);
    if (now - last < RELOAD_COOLDOWN_MS) {
      return false;
    }
    window.sessionStorage.setItem(LAST_RELOAD_KEY, String(now));
  } catch {
    // Without sessionStorage there is no loop guard, so leave the error to the boundary.
    return false;
  }
  window.location.reload();
  return true;
}

/** Recover chunk failures that reject outside a React error boundary (dynamic imports, prefetch). */
export function StaleBuildRecovery() {
  useEffect(() => {
    const onRejection = (event: PromiseRejectionEvent) => reloadOnceForStaleBuild(event.reason);
    const onError = (event: ErrorEvent) => reloadOnceForStaleBuild(event.error);
    window.addEventListener("unhandledrejection", onRejection);
    window.addEventListener("error", onError);
    return () => {
      window.removeEventListener("unhandledrejection", onRejection);
      window.removeEventListener("error", onError);
    };
  }, []);
  return null;
}
